import React from 'react'
import classnames from 'classnames'
import { FormattedMessage } from 'react-intl'
import { Checkbox, Tag, Typography } from 'antd'
import {
  CONSENT_TEXT_VERSIONS,
  FE_ROUTE,
  TEXT_UI_AUTH,
  TEXT_UI_CONSENT,
  TEXT_UI_LAYOUT,
} from 'config'
import styles from './style.module.scss'

// wording of policy version N = wording of the greatest version <= N that has its own locale texts
export const resolveConsentTextVersion = (policyVersion) => {
  const versions = CONSENT_TEXT_VERSIONS.filter((version) => version <= (policyVersion || 1))
  return versions.length ? Math.max(...versions) : CONSENT_TEXT_VERSIONS[0]
}

export const hasOptionalConsent = (policyVersion) => resolveConsentTextVersion(policyVersion) >= 2

export const toConsentsPayload = (value, policyVersion) => ({
  required: value?.required === true,
  ...(hasOptionalConsent(policyVersion) ? { optional: value?.optional === true } : {}),
})

const ExternalLink = ({ href, children }) => (
  <Typography.Link href={href} target="_blank" underline>
    {children}
  </Typography.Link>
)

export default function ConsentCheckboxes({
  policyVersion,
  value = {},
  onChange,
  showOptionalTag = false,
  showRequiredMark = false,
  className,
}) {
  const toggle = (field) => (event) => onChange?.({ ...value, [field]: event.target.checked })
  // same red asterisk as the other required fields of the register form
  const requiredMark = showRequiredMark && <span className={styles['required-mark']}>*</span>

  // V1: the old consent (paragraph + 1 checkbox)
  if (resolveConsentTextVersion(policyVersion) === 1)
    return (
      <div className={classnames(styles.consent, className)}>
        <Typography.Text>{TEXT_UI_AUTH.REGISTER.AGREE_WITH_TITLE}</Typography.Text>
        <Checkbox checked={value.required === true} onChange={toggle('required')}>
          <Typography.Text>
            {requiredMark}
            {TEXT_UI_AUTH.REGISTER.AGREE_WITH}&nbsp;
            <ExternalLink href={FE_ROUTE.MISC.TOS}>{TEXT_UI_LAYOUT.FOOTER.TOS}</ExternalLink>
            &nbsp;&&nbsp;
            <ExternalLink href={FE_ROUTE.MISC.PRIVACY}>
              {TEXT_UI_LAYOUT.FOOTER.PRIVACY}
            </ExternalLink>
          </Typography.Text>
        </Checkbox>
      </div>
    )

  const links = {
    tos: (
      <ExternalLink href={FE_ROUTE.MISC.TOS}>
        <FormattedMessage id={TEXT_UI_CONSENT.TOS_LINK} />
      </ExternalLink>
    ),
    privacy: (
      <ExternalLink href={FE_ROUTE.MISC.PRIVACY}>
        <FormattedMessage id={TEXT_UI_CONSENT.PRIVACY_LINK} />
      </ExternalLink>
    ),
    policy: (
      <ExternalLink href={FE_ROUTE.MISC.PRIVACY}>
        <FormattedMessage id={TEXT_UI_CONSENT.POLICY_LINK} />
      </ExternalLink>
    ),
  }

  return (
    <div className={classnames(styles.consent, className)}>
      <Typography.Text strong>
        <FormattedMessage id={TEXT_UI_CONSENT.V2.TITLE} />
      </Typography.Text>
      <Checkbox checked={value.required === true} onChange={toggle('required')}>
        <Typography.Text>
          {requiredMark}
          <FormattedMessage id={TEXT_UI_CONSENT.V2.REQUIRED} values={links} />
        </Typography.Text>
      </Checkbox>
      <Checkbox checked={value.optional === true} onChange={toggle('optional')}>
        {showOptionalTag && (
          <Tag className={styles['optional-tag']}>
            <FormattedMessage id={TEXT_UI_CONSENT.OPTIONAL_TAG} />
          </Tag>
        )}
        <Typography.Text>
          <FormattedMessage id={TEXT_UI_CONSENT.V2.OPTIONAL} values={links} />
        </Typography.Text>
      </Checkbox>
    </div>
  )
}

export { styles as consentStyles }
